/**
 * Alertas reales contra `/api/alerts`.
 *
 * El backend devuelve `cm_alerts` con shape ApiAlert; el endpoint ya filtra
 * por user.clientAccess. El `payload` es JSON libre — extraemos `info`,
 * `resourceId`, `actor` si vienen.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import type { ApiAlert } from "@/lib/adapters";
import type { ClientSlug } from "@/types";

export type AlertSeverity = "low" | "medium" | "high" | "critical";

export interface PanelAlert {
  id: number;
  kind: string;
  clientSlug: ClientSlug | null;
  severity: AlertSeverity;
  acknowledged: boolean;
  acknowledgedBy: string | null;
  message: string;
  resourceId: string | null;
  actor: string | null;
  createdAt: string;
}

export interface AlertsFilters {
  clientSlug?: ClientSlug;
  unresolvedOnly?: boolean;
  limit?: number;
}

function extractString(payload: unknown, key: string): string | null {
  if (!payload || typeof payload !== "object") return null;
  const value = (payload as Record<string, unknown>)[key];
  return typeof value === "string" ? value : null;
}

function buildMessage(payload: Record<string, unknown>): string {
  if (typeof payload.info === "string") return payload.info;
  if (typeof payload.message === "string") return payload.message;
  // Fallback: stringify reduce sin claves técnicas.
  const { resourceId: _r, actor: _a, ...rest } = payload;
  return JSON.stringify(rest);
}

function apiToPanel(api: ApiAlert): PanelAlert {
  const payload = (api.payload ?? {}) as Record<string, unknown>;
  return {
    id: api.id,
    kind: api.kind,
    clientSlug: (api.clientSlug as ClientSlug | null) ?? null,
    severity: api.severity,
    acknowledged: api.acknowledged,
    acknowledgedBy: api.acknowledgedBy ?? null,
    message: buildMessage(payload),
    resourceId: extractString(payload, "resourceId"),
    actor: extractString(payload, "actor"),
    createdAt: api.createdAt,
  };
}

export function useAlerts(filters: AlertsFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.clientSlug) qs.set("clientSlug", filters.clientSlug);
  if (filters.unresolvedOnly) qs.set("unresolvedOnly", "true");
  qs.set("limit", String(filters.limit ?? 50));

  return useQuery<PanelAlert[]>({
    queryKey: ["alerts", filters],
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await apiGet<{ items: ApiAlert[] }>(`/alerts?${qs.toString()}`);
      return res.items.map(apiToPanel);
    },
  });
}

export function useAckAlert() {
  const qc = useQueryClient();
  return useMutation<void, Error, { alertId: number }>({
    mutationFn: async ({ alertId }) => {
      await apiPost<{ ok: boolean }>("/alerts/ack", { alertId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
    onError: (err) => {
      toast.error(`No se pudo ackear: ${err.message}`);
    },
  });
}

export function useAckAllAlerts() {
  const qc = useQueryClient();
  return useMutation<
    { ackedCount: number; failedCount: number; failedIds: number[] },
    Error,
    { alertIds: number[] }
  >({
    mutationFn: async ({ alertIds }) => {
      // allSettled en vez de all: si una falla, las otras igual se ackean.
      // Antes con Promise.all un solo 5xx tiraba todo y dejaba alertas en
      // estado inconsistente (algunas ackeadas, otras no, sin feedback claro).
      const results = await Promise.allSettled(
        alertIds.map((alertId) =>
          apiPost<{ ok: boolean }>("/alerts/ack", { alertId }).then(() => alertId),
        ),
      );
      const ackedCount = results.filter((r) => r.status === "fulfilled").length;
      const failedIds = results
        .map((r, i) => (r.status === "rejected" ? alertIds[i]! : null))
        .filter((id): id is number => id !== null);
      return { ackedCount, failedCount: failedIds.length, failedIds };
    },
    onSuccess: ({ ackedCount, failedCount }) => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      if (failedCount === 0) {
        toast.success(`${ackedCount} alertas ackeadas`);
      } else {
        toast.warning(
          `${ackedCount} ackeadas · ${failedCount} fallaron (reintenta)`,
        );
      }
    },
    onError: (err) => {
      toast.error(`Error: ${err.message}`);
    },
  });
}
